import { useTranslation } from 'react-i18next';
import { Sparkles, Brain, Puzzle, ShieldCheck } from 'lucide-react';
import { Reveal, RevealGroup, RevealItem } from './Reveal';

/** Three distinct capability cards below the main Claude card */
const CAPABILITY_CARDS = [
  { key: 'smartModel', Icon: Brain },
  { key: 'skillSystem', Icon: Puzzle },
  { key: 'crossPlatform', Icon: ShieldCheck },
] as const;

export default function ProviderCapabilities() {
  const { t } = useTranslation();
  const claudeFeatures = t('providers.claude.features', { returnObjects: true }) as string[];

  return (
    <section className="py-24 md:py-32 bg-[#f0eee6] dark:bg-[#1a1918]">
      <div className="mx-auto max-w-6xl px-6">

        {/* Section header */}
        <Reveal className="mb-16 text-center">
          <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-[#1414131a] dark:border-[#faf9f51a] px-4 py-1.5 text-[#6b6a68] dark:text-[#9a9893]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ae5630]" />
            {t('providers.title')}
          </span>
          <h2 className="font-display display-tight mt-5 text-3xl font-medium leading-[1.1] text-[#141413] dark:text-[#faf9f5] md:text-5xl">
            {t('providers.title')}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-[#6b6a68] dark:text-[#9a9893]">
            {t('providers.subtitle')}
          </p>
        </Reveal>

        {/* Claude main card — non-symmetric bento: wider left feature */}
        <Reveal className="mb-8">
          <div className="rounded-[1.5rem] border border-[#1414131a] dark:border-[#faf9f51a] bg-[#1414130a] dark:bg-[#faf9f50a] p-1.5 shadow-warm">
            <div className="rounded-[calc(1.5rem-0.375rem)] bg-[#fffefb] dark:bg-[#1a1918] shadow-inner-glow">
              <div className="grid grid-cols-1 lg:grid-cols-5">
                {/* Left — brand accent column */}
                <div className="flex flex-col justify-between gap-6 border-b border-[#1414130d] dark:border-[#faf9f50d] p-8 lg:col-span-2 lg:border-b-0 lg:border-r">
                  <div>
                    <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#ae5630]/10 dark:bg-[#ae5630]/15 text-[#ae5630]">
                      <Sparkles size={22} />
                    </div>
                    <h3 className="font-display display-tight text-2xl font-medium leading-[1.1] text-[#141413] dark:text-[#faf9f5]">
                      {t('providers.claude.title')}
                    </h3>
                    <p className="mt-3 text-[#6b6a68] dark:text-[#9a9893] leading-relaxed">
                      {t('providers.claude.desc')}
                    </p>
                  </div>
                  {/* Decorative clay accent bar */}
                  <div className="h-1 w-16 rounded-full bg-[#ae5630]/30" />
                </div>

                {/* Right — feature bullet grid */}
                <div className="flex items-center p-8 lg:col-span-3">
                  <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 w-full">
                    {claudeFeatures.map((feat, fi) => (
                      <li key={fi} className="flex items-start gap-3 text-sm text-[#6b6a68] dark:text-[#9a9893] leading-relaxed">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#ae5630]" />
                        {feat}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Three capability cards — non-symmetric: 1 wide + 2 normal */}
        <RevealGroup className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {CAPABILITY_CARDS.map(({ key, Icon }, i) => (
            <RevealItem
              key={key}
              className={`group rounded-2xl border border-[#1414131a] dark:border-[#faf9f51a] bg-[#fffefb] dark:bg-[#1a1918] shadow-warm transition-all duration-300 ease-spring hover:shadow-warm-lg hover:border-[#ae5630]/30 dark:hover:border-[#ae5630]/30 ${i === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
            >
              <div className="p-7 h-full flex flex-col gap-4">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#ae5630]/10 dark:bg-[#ae5630]/15 text-[#ae5630] transition-colors duration-300 group-hover:bg-[#ae5630]/20 dark:group-hover:bg-[#ae5630]/25">
                  <Icon size={20} />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg font-medium text-[#141413] dark:text-[#faf9f5] mb-2">
                    {t(`providers.capabilities.${key}.title`)}
                  </h3>
                  <p className="text-sm text-[#6b6a68] dark:text-[#9a9893] leading-relaxed">
                    {t(`providers.capabilities.${key}.desc`)}
                  </p>
                </div>
              </div>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>
  );
}
